import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarHeart, X, Gift } from "lucide-react";
import { API, SITE_API_KEY } from "../lib/store";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { toast } from "sonner";

const OCCASIONS = ["Anniversary", "Birthday", "Wedding", "Engagement", "Religious Celebration", "Other"];

function loadOccasions() {
  try { return JSON.parse(localStorage.getItem("rj_occasions") || "[]"); } catch (e) { return []; }
}

export function OccasionDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ occasion: "Anniversary", date: "", name: "", email: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date) return toast.error("Please pick the date");
    try {
      await fetch(`${API}/occasions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": SITE_API_KEY },
        body: JSON.stringify(form),
      });
    } catch (err) {
      console.debug("occasion save:", err?.message);
    }
    const list = loadOccasions();
    list.push({ occasion: form.occasion, date: form.date, name: form.name });
    localStorage.setItem("rj_occasions", JSON.stringify(list.slice(-10)));
    toast.success("Mr. Regal will remember this date.");
    onOpenChange(false);
    setForm({ occasion: "Anniversary", date: "", name: "", email: "" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#fffdf8] border-[#e8e0d0] rounded-none sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-wide text-[#1a1713]">A Date Worth Remembering</DialogTitle>
          <DialogDescription className="text-sm text-[#7a7160]">
            Mr. Regal will gently remind you in time to find the perfect piece.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Occasion</Label>
              <select value={form.occasion} onChange={set("occasion")} className="w-full h-10 border border-[#d8cdb5] bg-white px-2.5 text-sm text-[#171310]" data-testid="occasion-type-select">
                {OCCASIONS.map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Date *</Label>
              <Input type="date" value={form.date} onChange={set("date")} className="rounded-none border-[#d8cdb5]" data-testid="occasion-date-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Your Name</Label>
              <Input value={form.name} onChange={set("name")} placeholder="Optional" className="rounded-none border-[#d8cdb5]" data-testid="occasion-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Email</Label>
              <Input type="email" value={form.email} onChange={set("email")} placeholder="For a reminder email" className="rounded-none border-[#d8cdb5]" data-testid="occasion-email-input" />
            </div>
          </div>
          <p className="text-[11px] text-[#8f8571]">
            Add your email and Mr. Regal will write to you two weeks before the date. We never share your details.
          </p>
          <Button type="submit" className="w-full rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 transition-colors duration-300" data-testid="occasion-save-btn">
            Remember This Date
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function OccasionCard({ className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={className}>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-[#8a6a1f] border border-[#c9a24b]/60 px-4 py-2.5 hover:bg-[#c9a24b]/10 transition-colors"
        data-testid="occasion-open-btn"
      >
        <CalendarHeart size={13} /> Save a Special Date — Mr. Regal Will Remember
      </button>
      <OccasionDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

// Gentle on-site reminder when a saved occasion is within 21 days
export function OccasionBar() {
  const [hit, setHit] = useState(() => {
    if (sessionStorage.getItem("rj_occasion_bar_shown")) return null;
    const now = new Date();
    for (const o of loadOccasions()) {
      const d = new Date(`${o.date}T00:00:00`);
      if (isNaN(d)) continue;
      let next = new Date(now.getFullYear(), d.getMonth(), d.getDate());
      if (next < new Date(now.getFullYear(), now.getMonth(), now.getDate())) {
        next = new Date(now.getFullYear() + 1, d.getMonth(), d.getDate());
      }
      const days = Math.round((next - now) / 86400000);
      const key = `rj_occ_dismissed_${o.occasion}_${next.getFullYear()}`;
      if (days >= 0 && days <= 21 && !localStorage.getItem(key)) return { ...o, days, next, key };
    }
    return null;
  });
  if (!hit) return null;
  const dismiss = () => {
    localStorage.setItem(hit.key, "1");
    sessionStorage.setItem("rj_occasion_bar_shown", "1");
    setHit(null);
  };
  const when = hit.days === 0 ? "today" : hit.days === 1 ? "tomorrow" : `in ${hit.days} days — ${hit.next.toLocaleDateString("en-US", { month: "long", day: "numeric" })}`;
  return (
    <div
      className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 w-max max-w-[min(92vw,540px)] bg-white border border-[#c9a24b]/60 shadow-[0_14px_40px_-14px_rgba(26,23,19,0.4)] px-4 py-3"
      data-testid="occasion-bar"
      aria-live="polite"
    >
      <img src="/mr-regal-idea.webp" alt="" aria-hidden="true" className="h-12 w-auto shrink-0" />
      <p className="text-xs italic text-[#3f3a30] leading-relaxed min-w-0">
        “{hit.name ? `${hit.name}, an` : "An"} important date approaches — your {hit.occasion.toLowerCase()} is {when}. Shall we find something special?”
      </p>
      <Link to="/shop" onClick={dismiss} className="shrink-0 inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase bg-[#171310] text-[#e9d9ac] px-3 py-2 hover:bg-[#3a2f1d] transition-colors" data-testid="occasion-bar-shop">
        <Gift size={11} /> Find a Gift
      </Link>
      <button onClick={dismiss} aria-label="Dismiss reminder" className="text-[#8f8571] hover:text-[#171310] shrink-0" data-testid="occasion-bar-close">
        <X size={14} />
      </button>
    </div>
  );
}
